import { useEffect, useState } from "react";
import { RefreshCw, CheckCircle2, XCircle, Radio, FileWarning, BookOpen, Plus, Power, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

/* Admin — SocialPulse feed Source Health console.
   Registry state, item lifecycle counts, freshness and ingest event outcomes.
   Partner sources (e.g. Kerala.com) are added/toggled here — no redeploy. */

const MODULES = ["food", "travel", "today", "family", "news"];

export default function AdminPulseSources() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [form, setForm] = useState({ source_id: "", name: "", domain: "", modules: ["news"] });
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/pulse/admin/sources").then((r) => setData(r.data)).catch(() => setErr(true));
  useEffect(() => { load(); }, []);

  const addSource = async () => {
    setBusy(true);
    try {
      await api.post("/pulse/admin/sources", form);
      toast.success(`Source "${form.name}" registered — share the ingest secret with them`);
      setForm({ source_id: "", name: "", domain: "", modules: ["news"] });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not add the source"); }
    setBusy(false);
  };

  const toggle = async (s) => {
    try {
      await api.put(`/pulse/admin/sources/${s.source_id}`, { enabled: !s.enabled });
      toast.success(`${s.name} ${s.enabled ? "disabled — its cards stop appearing" : "enabled"}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update"); }
  };

  const removeSource = async (s) => {
    if (!window.confirm(`Delete partner source "${s.name}"? Its ingested content is withdrawn from the feed.`)) return;
    try { await api.delete(`/pulse/admin/sources/${s.source_id}`); toast.success("Source deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not delete"); }
  };

  if (err) return <div className="max-w-5xl mx-auto px-5 py-10 text-sm text-zinc-500">Could not load source health (moderator access required).</div>;
  if (!data) return <div className="max-w-5xl mx-auto px-5 py-10 text-sm text-zinc-500">Loading source health…</div>;

  const fmt = (iso) => iso ? new Date(iso).toLocaleString() : "—";
  return (
    <div className="max-w-5xl mx-auto px-5 py-8" data-testid="admin-pulse-sources">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="font-heading font-900 text-3xl tracking-tight flex items-center gap-2"><Radio className="w-7 h-7 text-blue-700" /> Feed Source Health</h1>
        <button onClick={load} data-testid="aps-refresh" className="ml-auto inline-flex items-center gap-1.5 text-sm font-700 border border-zinc-300 rounded-lg px-3 py-1.5 hover:border-blue-500"><RefreshCw className="w-4 h-4" /> Refresh</button>
      </div>

      <div className="mt-4 flex flex-wrap gap-2 text-xs" data-testid="aps-status-chips">
        <Chip ok={data.ingest_configured} okText="Ingest secret configured" badText="Ingest NOT configured" />
        <Chip ok={!data.fixtures_enabled} okText="Fixtures OFF (production mode)" badText="Dev fixtures ENABLED — sample cards visible" warn />
        <span className={`inline-flex items-center gap-1 rounded-full px-3 py-1.5 border font-700 ${data.open_reports > 0 ? "bg-rose-50 border-rose-300 text-rose-700" : "bg-zinc-50 border-zinc-300 text-zinc-600"}`} data-testid="aps-reports">
          <FileWarning className="w-3.5 h-3.5" /> {data.open_reports} user report{data.open_reports === 1 ? "" : "s"}
        </span>
      </div>

      <div className="mt-5 space-y-3">
        {data.sources.map((s) => (
          <div key={s.source_id} className={`bg-white border rounded-2xl p-4 ${s.enabled ? "border-zinc-200" : "border-dashed border-zinc-300 opacity-70"}`} data-testid={`aps-source-${s.source_id}`}>
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-heading font-800">{s.name}</span>
              <span className="text-xs text-zinc-400 font-mono">{s.domain}</span>
              {s.custom && <span className="text-[10px] font-800 uppercase bg-blue-50 text-blue-700 border border-blue-300 rounded px-1.5 py-0.5">Partner</span>}
              {s.enabled
                ? <span className="text-[10px] font-800 uppercase bg-emerald-50 text-emerald-700 border border-emerald-300 rounded px-1.5 py-0.5">Enabled</span>
                : <span className="text-[10px] font-800 uppercase bg-zinc-100 text-zinc-500 border border-zinc-300 rounded px-1.5 py-0.5">Disabled</span>}
              <span className="text-xs text-zinc-400 ml-auto">modules: {s.modules.join(", ")}</span>
              <button onClick={() => toggle(s)} data-testid={`aps-toggle-${s.source_id}`}
                title={s.enabled ? "Disable this source" : "Enable this source"}
                className={`inline-flex items-center gap-1 text-[11px] font-700 border rounded-lg px-2 py-1 ${s.enabled ? "border-zinc-300 text-zinc-600 hover:border-rose-400 hover:text-rose-600" : "border-emerald-300 text-emerald-700 hover:bg-emerald-50"}`}>
                <Power className="w-3 h-3" /> {s.enabled ? "Disable" : "Enable"}
              </button>
              {s.custom && (
                <button onClick={() => removeSource(s)} data-testid={`aps-delete-${s.source_id}`}
                  className="text-zinc-300 hover:text-rose-600 p-1" title="Delete partner source"><Trash2 className="w-3.5 h-3.5" /></button>
              )}
            </div>
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
              <Stat label="Active items" value={s.items_by_status.active || 0} />
              <Stat label="Withdrawn/deleted" value={(s.items_by_status.withdrawn || 0) + (s.items_by_status.deleted || 0) + (s.items_by_status.expired || 0)} />
              <Stat label="Past expiry (auto-hidden)" value={s.expired_by_time} warn={s.expired_by_time > 0} />
              <Stat label="Fixture (sample) items" value={s.fixture_items} warn={s.fixture_items > 0} />
            </div>
            <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-xs text-zinc-500">
              <span>Newest item: <b className="text-zinc-700">{fmt(s.newest_item_at)}</b></span>
              <span>Last ingest event: <b className="text-zinc-700">{fmt(s.last_event_at)}</b></span>
              <span>Events: {Object.keys(s.events).length === 0 ? "none yet" :
                Object.entries(s.events).map(([k, v]) => `${k}: ${v}`).join(" · ")}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6 bg-white border border-zinc-200 rounded-2xl p-4" data-testid="aps-add-source">
        <div className="font-heading font-800 flex items-center gap-2"><Plus className="w-4 h-4 text-blue-700" /> Add a partner source</div>
        <p className="mt-1 text-xs text-zinc-500">Register a sister/partner site (e.g. Kerala.com). They publish through the same signed webhook — no deploy needed.</p>
        <div className="mt-3 flex flex-wrap gap-2">
          <input value={form.source_id} onChange={(e) => setForm((f) => ({ ...f, source_id: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, "") }))}
            placeholder="source_id — e.g. kerala" data-testid="aps-form-id"
            className="w-44 border border-zinc-300 rounded-lg px-3 py-2.5 text-sm font-mono" />
          <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="Display name — e.g. Kerala.com" data-testid="aps-form-name"
            className="flex-1 min-w-[160px] border border-zinc-300 rounded-lg px-3 py-2.5 text-sm" />
          <input value={form.domain} onChange={(e) => setForm((f) => ({ ...f, domain: e.target.value }))}
            placeholder="Domain — kerala.com" data-testid="aps-form-domain"
            className="w-48 border border-zinc-300 rounded-lg px-3 py-2.5 text-sm font-mono" />
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <span className="text-xs font-700 text-zinc-500">Feed modules:</span>
          {MODULES.map((m) => (
            <label key={m} className={`inline-flex items-center gap-1 text-xs font-600 border rounded-full px-2.5 py-1 cursor-pointer ${form.modules.includes(m) ? "bg-blue-50 border-blue-400 text-blue-800" : "border-zinc-300 text-zinc-500"}`}>
              <input type="checkbox" className="hidden" checked={form.modules.includes(m)} data-testid={`aps-form-module-${m}`}
                onChange={() => setForm((f) => ({ ...f, modules: f.modules.includes(m) ? f.modules.filter((x) => x !== m) : [...f.modules, m] }))} />
              {m}
            </label>
          ))}
          <button onClick={addSource} disabled={busy || !form.source_id || !form.name || !form.domain} data-testid="aps-form-submit"
            className="ml-auto inline-flex items-center gap-1.5 bg-blue-700 text-white px-4 py-2 text-sm font-700 rounded-lg hover:bg-blue-800 disabled:opacity-40">
            <Plus className="w-4 h-4" /> Register source
          </button>
        </div>
      </div>

      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-2xl p-4 text-sm" data-testid="aps-guide">
        <div className="font-heading font-800 flex items-center gap-2"><BookOpen className="w-4 h-4 text-blue-700" /> Onboarding a real source</div>
        <p className="mt-1 text-zinc-700">Sister sites publish via signed webhooks: <code className="bg-white border border-blue-200 rounded px-1">POST /api/pulse/ingest/&#123;source_id&#125;</code> with <code className="bg-white border border-blue-200 rounded px-1">X-Pulse-Event-Id</code> + <code className="bg-white border border-blue-200 rounded px-1">X-Pulse-Signature</code> (HMAC-SHA256 of the raw body). Idempotent events, monotonic versions, lifecycle statuses and offer expiry are enforced server-side.</p>
        <p className="mt-1.5 text-zinc-700">Full contract with payload examples: <b>docs/PULSE_INGEST_GUIDE.md</b> in the repository. Share it with the FoodPulse team together with the private ingest secret — once real events flow, turn <code className="bg-white border border-blue-200 rounded px-1">PULSE_FIXTURES_ENABLED</code> off.</p>
      </div>
    </div>
  );
}

function Chip({ ok, okText, badText, warn }) {
  return ok ? (
    <span className="inline-flex items-center gap-1 rounded-full px-3 py-1.5 border font-700 bg-emerald-50 border-emerald-300 text-emerald-700"><CheckCircle2 className="w-3.5 h-3.5" /> {okText}</span>
  ) : (
    <span className={`inline-flex items-center gap-1 rounded-full px-3 py-1.5 border font-700 ${warn ? "bg-amber-50 border-amber-300 text-amber-700" : "bg-rose-50 border-rose-300 text-rose-700"}`}><XCircle className="w-3.5 h-3.5" /> {badText}</span>
  );
}

function Stat({ label, value, warn }) {
  return (
    <div className="bg-zinc-50 border border-zinc-200 rounded-xl px-3 py-2">
      <div className={`font-mono font-800 text-lg ${warn ? "text-amber-700" : "text-zinc-800"}`}>{value}</div>
      <div className="text-[11px] text-zinc-500">{label}</div>
    </div>
  );
}
